import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Question } from '@/contexts/InterviewContext'
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react'

interface TextQuestionCardProps {
  question: Question
  currentIndex: number
  totalQuestions: number
  onAnswerSubmit: (optionId: string) => Promise<boolean>
  selectedOption: string | null
  onPrevious: () => void
  onNext: () => void
  onSubmit: () => void
  hasResponse: boolean
  isLast: boolean
  isSubmitting: boolean
  isLocked?: boolean
  isDisabled?: boolean
}

const TextQuestionCard = ({
  question,
  currentIndex,
  totalQuestions,
  onAnswerSubmit,
  selectedOption,
  onPrevious,
  onNext,
  onSubmit,
  isLast,
  isSubmitting = false,
  isLocked = false,
  isDisabled = false,
}: TextQuestionCardProps) => {
  // This state now primarily serves to provide immediate UI feedback.
  const [tempSelection, setTempSelection] = useState<string | null>(selectedOption)
  const [isSaving, setIsSaving] = useState(false)

  // When the question changes, sync the local selection with the parent's state.
  // Adding `currentIndex` ensures it runs when navigating back and forth.
  useEffect(() => {
    setTempSelection(selectedOption)
  }, [selectedOption, currentIndex])

  /**
   * [THE FIX - PART 1]
   * This function is called the moment a user clicks a radio button.
   * It updates both the local UI and the parent component's state simultaneously.
   */
  const handleSelectionChange = async (optionId: string) => {
    const previousSelection = tempSelection
    setTempSelection(optionId)
    setIsSaving(true)
    try {
      const saved = await onAnswerSubmit(optionId)
      if (!saved) setTempSelection(previousSelection)
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * [THE FIX - PART 2]
   * This function is now simplified and is only responsible for navigation
   * or triggering the final submission. It no longer sends answer data.
   */
  const handleNavigation = () => {
    if (isLast) {
      // The final answer is already saved. We can safely submit the entire exam.
      onSubmit()
    } else {
      onNext()
    }
  }

  return (
    <Card className="w-full max-w-3xl mx-auto shadow-md">
      <CardHeader className="border-b bg-gray-50">
        <CardTitle className="text-lg font-medium">
          Soru {currentIndex + 1} / {totalQuestions}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6">
        <div className="mb-6">
          {question.image_path && (
            <div className="mb-4">
              <img
                src={question.image_path}
                alt="Soru görseli"
                className="w-full max-h-96 object-contain rounded-md shadow"
                key={question.image_path}
              />
            </div>
          )}
          <div
            dangerouslySetInnerHTML={{ __html: question.text }}
            className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ol:my-2 prose-ul:my-2 prose-li:my-0.5 mb-6"
          />

          {isLocked && (
            <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-md w-fit">
              <Check size={12} />
              Cevaplandı — değiştirilemez
            </div>
          )}
          <RadioGroup
            value={tempSelection || ''}
            onValueChange={isLocked || isDisabled || isSaving ? undefined : handleSelectionChange}
            className="space-y-3"
          >
            {question.choices.map((option) => (
              <div
                key={option.id}
                className={`flex items-center space-x-2 border rounded-md p-3 transition-colors ${isLocked || isDisabled || isSaving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <RadioGroupItem
                  value={option.id}
                  id={`option-${option.id}`}
                  disabled={isLocked || isDisabled || isSaving}
                />
                <Label
                  htmlFor={`option-${option.id}`}
                  className={`flex-1 ${isLocked || isDisabled || isSaving ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="font-medium mr-2">{option.label.toUpperCase()}.</span>
                  <span>{option.text}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* The controls section now correctly uses the simplified navigation handler. */}
        <div className="w-full mt-6 flex justify-between">
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={currentIndex === 0 || isSubmitting || isDisabled || isSaving}
          >
            <ChevronLeft size={16} className="mr-2" />
            Önceki
          </Button>

          <div className="flex gap-2">
            {isLast ? (
              <Button
                onClick={handleNavigation}
                disabled={isSubmitting || isDisabled || isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sonuçlandırılıyor...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    Tamamla
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNavigation}
                disabled={isSubmitting || isDisabled || isSaving}
                className="bg-interview hover:bg-interview-dark"
              >
                İleri
                <ChevronRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default TextQuestionCard
